/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Constants
export const DELIVERY_OPTIONS = {
    DELIVERY: 'delivery',
    PICKUP: 'pickup',
} as const;

export type DeliveryOption = (typeof DELIVERY_OPTIONS)[keyof typeof DELIVERY_OPTIONS];

export const PICKUP_SHIPPING_METHOD_ID = '005';

export const PICKUP_SHIPMENT_ID = 'pickup';

/**
 * The search refinement that filters a grid to products a given store carries.
 *
 * The VALUE refined on is the STORE ID (`selectedStoreInfo.id`, e.g. `1010`) — not
 * the store's inventory-list id. Product values are authored from the live per-store
 * inventory lists, so a product carries a store id if and only if that store's list
 * holds an allocation for it. See
 * `.reference/data-authored-store-refinement/README.md`.
 *
 * THIS REPLACES `ilids`, WHICH NEVER WORKED HERE AND CANNOT. Measured against
 * bkcb-004 on 2026-08-23, SCAPI ShopperSearch rejects `ilids` as a top-level query
 * parameter outright:
 *
 *     ?ilids=inventory_store_nm_1012   -> HTTP 400
 *     {"detail":"Query parameter 'ilids' is not defined in the API"}
 *
 * and silently ignores it in the `refine=` form — a real list id, a different real
 * list id and a nonsense string all returned the same 23 hits for NM `q=dress`,
 * against a control of `refine=price=(0..200)` returning 1. `ilids` is a SFRA
 * `dw.catalog.ProductSearchModel` parameter (Shop the Store); it is not in the SCAPI
 * contract, so no site preference or feature switch reaches it from a headless
 * storefront.
 *
 * The replacement discriminates, and fails CLOSED:
 *
 *     NM q=dress  baseline 23 | store 1010 -> 12 | 1001 -> 5 | 1029 -> 6 | nonsense -> 0
 *     BG q=dress  baseline  8 | store 1063 ->  4 |                        nonsense -> 0
 */
export const STORE_AVAILABILITY_REFINEMENT = 'c_availableStores';
